import React, { useState, useEffect } from 'react';
import { songService } from '../../../services/musicService';

function PlaylistForm({ initialData, onSubmit, onCancel, title = 'Add Playlist' }) {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    is_public: true,
    song_ids: []
  });
  const [songs, setSongs] = useState([]);
  const [selectedSongs, setSelectedSongs] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchSongs();
    
    if (initialData) {
      setFormData({
        name: initialData.name || '',
        description: initialData.description || '',
        is_public: initialData.is_public !== undefined ? initialData.is_public : true,
        song_ids: initialData.songs?.map(song => song.song_id || song.id) || []
      });
      setSelectedSongs(initialData.songs || []);
    }
  }, [initialData]);

  const fetchSongs = async () => {
    try {
      setLoading(true);
      const response = await songService.getAllSongs({ limit: 100 });
      setSongs(response.data?.data || []);
    } catch (error) {
      console.error('Failed to fetch songs:', error);
    } finally {
      setLoading(false);
    }
  };

  const validate = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (formData.name.length > 255) {
      newErrors.name = 'Name must be less than 255 characters';
    }
    
    return newErrors;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    
    const submitData = {
      ...formData,
      description: formData.description || null,
      song_ids: selectedSongs.map(song => song.song_id || song.id)
    };
    
    onSubmit(submitData);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSongSelect = (song) => {
    if (selectedSongs.find(s => s.id === song.id || s.song_id === song.song_id)) {
      setSelectedSongs(prev => prev.filter(s => s.id !== song.id && s.song_id !== song.song_id));
    } else {
      setSelectedSongs(prev => [...prev, song]);
    }
  };

  const handleRemoveSong = (songId) => {
    setSelectedSongs(prev => prev.filter(song => 
      song.id !== songId && song.song_id !== songId
    ));
  };

  const filteredSongs = songs.filter(song => 
    song.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    song.artist?.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="bg-gray-900/30 backdrop-blur-sm border border-gray-800 rounded-2xl p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-white">{title}</h2>
        <button
          onClick={onCancel}
          className="text-gray-400 hover:text-white"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
      
      <form onSubmit={handleSubmit}>
        <div className="space-y-4">
          <div>
            <label className="block text-gray-300 mb-2">Name *</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className={`w-full px-4 py-3 bg-gray-800/50 border ${errors.name ? 'border-red-500' : 'border-gray-700'} rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500`}
              placeholder="Enter playlist name"
            />
            {errors.name && (
              <p className="mt-1 text-red-400 text-sm">{errors.name}</p>
            )}
          </div>
          
          <div>
            <label className="block text-gray-300 mb-2">Description</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows="3"
              className="w-full px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500 resize-none"
              placeholder="Enter playlist description (optional)"
            />
          </div>
          
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              name="is_public"
              checked={formData.is_public}
              onChange={handleChange}
              className="rounded border-gray-600 bg-gray-800"
            />
            <label className="text-gray-300">Make playlist public</label>
          </div>
          
          <div>
            <label className="block text-gray-300 mb-2">Add Songs</label>
            <div className="mb-4">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search songs..."
                className="w-full px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500"
              />
            </div>
            
            {/* Selected Songs */}
            {selectedSongs.length > 0 && (
              <div className="mb-6">
                <p className="text-gray-300 mb-2">Selected Songs ({selectedSongs.length})</p>
                <div className="space-y-2 max-h-48 overflow-y-auto p-2 bg-gray-900/30 rounded-xl">
                  {selectedSongs.map(song => (
                    <div key={song.id || song.song_id} className="flex items-center justify-between p-3 bg-gray-800/50 rounded-lg">
                      <div>
                        <p className="text-white">{song.title}</p>
                        <p className="text-gray-400 text-sm">{song.artist?.name || 'Unknown Artist'}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRemoveSong(song.id || song.song_id)}
                        className="text-red-400 hover:text-red-300"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Available Songs */}
            <div>
              <p className="text-gray-300 mb-2">Available Songs</p>
              {loading ? (
                <div className="text-center py-8">
                  <div className="inline-block w-8 h-8 border-2 border-purple-500/30 border-t-purple-500 rounded-full animate-spin"></div>
                  <p className="text-gray-400 mt-2">Loading songs...</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-64 overflow-y-auto p-2 bg-gray-900/30 rounded-xl">
                  {filteredSongs.map(song => (
                    <div
                      key={song.id || song.song_id}
                      onClick={() => handleSongSelect(song)}
                      className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-colors ${
                        selectedSongs.find(s => s.id === song.id || s.song_id === song.song_id)
                          ? 'bg-purple-600/20 border border-purple-700/50'
                          : 'bg-gray-800/50 hover:bg-gray-700/50'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          selectedSongs.find(s => s.id === song.id || s.song_id === song.song_id)
                            ? 'bg-purple-600'
                            : 'bg-gray-700'
                        }`}>
                          <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                          </svg>
                        </div>
                        <div>
                          <p className="text-white">{song.title}</p>
                          <p className="text-gray-400 text-sm">
                            {song.artist?.name || 'Unknown Artist'} • {Math.floor(song.duration_seconds / 60)}:{String(song.duration_seconds % 60).padStart(2, '0')}
                          </p>
                        </div>
                      </div>
                      <div className="text-gray-400 text-sm">
                        {song.genre}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
        
        <div className="flex gap-3 mt-8">
          <button
            type="submit"
            className="flex-1 px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-xl transition-all font-medium"
          >
            {initialData ? 'Update Playlist' : 'Create Playlist'}
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 px-6 py-3 bg-gray-800/50 hover:bg-gray-700/50 text-gray-300 rounded-xl transition-all border border-gray-700"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

export default PlaylistForm;